import { z } from 'zod';
import { AuditEntityType, SortDirection } from '@trulyinbox/types';
import { multiValueFilter } from '@/features/email-accounts/search-schema';
import { AUDIT_ACTIONS, AUDIT_MODULES } from '@/lib/audit-logs';

/** Rows-per-page default. Kept in sync with the backend list default. */
export const DEFAULT_AUDIT_PAGE_SIZE = 15;

/**
 * Rows-per-page options for the audit table. Must include
 * `DEFAULT_AUDIT_PAGE_SIZE` — the pagination `<Select>` renders blank when its
 * current value isn't one of the offered options.
 */
export const AUDIT_PAGE_SIZE_OPTIONS = [15, 25, 50, 100];

/**
 * URL search-parameter schema for the `/audit-logs` route. The URL is the single
 * source of truth for filters, pagination, sort, and the open-detail entry, so a
 * refresh / deep link / back button reproduces the same view.
 *
 * - `module` / `action` → multi-select, normalised exactly like the
 *   email-accounts `status`/`provider` filters (reusing the shared
 *   `multiValueFilter`): a lone value coerces to a one-element array,
 *   unknown/blank members are dropped + deduped, and an empty set collapses to
 *   `undefined` so an inactive filter leaves no URL key.
 * - `entityType` → single-select over `AuditEntityType`; an unknown value is
 *   dropped (the backend DTO is `@IsEnum`-validated, so passing it through
 *   would 400 the list request rather than return an empty page).
 * - `dateFrom` / `dateTo` → ISO `YYYY-MM-DD` bounds on `createdAt`.
 * - `page` / `limit` → offset pagination (`limit` capped at the server's 100).
 * - `sort` → `asc | desc` on `createdAt`, defaulting to newest-first.
 * - `logId` → the id of the entry whose before→after diff dialog is open.
 */
export const auditLogsSearchSchema = z.object({
  module: multiValueFilter(AUDIT_MODULES),
  action: multiValueFilter(AUDIT_ACTIONS),
  actorUserId: z.coerce.number().int().positive().optional().catch(undefined),
  entityType: z.nativeEnum(AuditEntityType).optional().catch(undefined),
  dateFrom: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .catch(undefined),
  dateTo: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .catch(undefined),
  page: z.coerce.number().int().min(1).catch(1),
  limit: z.coerce.number().int().min(1).max(100).catch(DEFAULT_AUDIT_PAGE_SIZE),
  sort: z.nativeEnum(SortDirection).catch(SortDirection.DESC),
  logId: z.coerce.number().int().positive().optional().catch(undefined),
});

export type AuditLogsSearch = z.infer<typeof auditLogsSearchSchema>;
